import { useQuery } from "react-query";

import * as ajaxEndpoints from "../api/ajax-endpoints";
import { getData } from "../newApis/dotNetApiMethods";
import { toDepositTypeId } from "../helpers/depositType";

/**
 * Core banking products for a deposit type, in one shape.
 *
 * `GET /misc/cba/products` returns `dynamic` on the backend and picks a
 * different DTO per `producttype`, with different JSON casing:
 *
 * - 3 Fixed and 4 Recurring — `AdvanclyDepositProductResponseDto`, snake_case
 *   through `[JsonPropertyName]`: `currency_code`, `minimum_investment`.
 * - 2 Savings — `CBASavingsProductResponse`, which carries no such attributes
 *   and so comes back camelCase: `currencyCode`, and no deposit limits at all.
 *
 * Both call sites filtered on `m.currency_code` behind an `any`, so for
 * savings the filter matched nothing and the "Mifos Product" dropdown rendered
 * empty — with no error, because a 200 carrying six products is not a failure.
 * Normalising here means no call site has to know which type it asked for.
 */

/** What the fixed and recurring deposit endpoints return. */
type TDepositProductResponse = {
  id: number;
  name: string;
  short_name?: string;
  description?: string;
  currency_code?: string;
  minimum_investment?: number;
  maximum_investment?: number;
  minDepositTerm?: number;
  maxDepositTerm?: number;
  number_of_days_in_year?: number;
  interest_rate_chart?: unknown[];
};

/** What the savings endpoint returns. Note: no limits, no term, no chart. */
type TSavingsProductResponse = {
  id: number;
  name: string;
  shortName?: string;
  description?: string;
  currencyCode?: string;
  nominalAnnualInterestRate?: number;
  interestCalculationDaysInYearTypeEnum?: number;
  minRequiredOpeningBalance?: number | null;
};

type TMifosProductResponse = TDepositProductResponse | TSavingsProductResponse;

/** One product, however the backend spelled it. */
export type TMifosProduct = {
  id: number;
  name: string;
  shortName?: string;
  description?: string;
  currencyCode?: string;
  /**
   * Null for a savings product — Mifos savings accounts have no minimum or
   * maximum deposit, so the admin types them instead. Creation requires both
   * to be greater than zero either way.
   */
  minimumInvestment: number | null;
  maximumInvestment: number | null;
  /** Null for savings: a savings account has no term. */
  minDepositTerm: number | null;
  maxDepositTerm: number | null;
  numberOfDaysInYear: number | null;
  /** The flat annual rate a savings product carries; null for fixed/recurring. */
  nominalAnnualInterestRate: number | null;
};

/** @deprecated Kept for the older call sites. Prefer `TMifosProduct`. */
export type TMisfoProduct = TMifosProduct;

type TMifosProductQuery = {
  productType?: string | number;
  tenantId?: string;
};

export function normaliseMifosProduct(
  product: TMifosProductResponse,
): TMifosProduct {
  const deposit = product as TDepositProductResponse;
  const savings = product as TSavingsProductResponse;

  return {
    id: product.id,
    name: product.name,
    shortName: deposit.short_name ?? savings.shortName,
    description: product.description,
    currencyCode: deposit.currency_code ?? savings.currencyCode,
    minimumInvestment: deposit.minimum_investment ?? null,
    maximumInvestment: deposit.maximum_investment ?? null,
    minDepositTerm: deposit.minDepositTerm ?? null,
    maxDepositTerm: deposit.maxDepositTerm ?? null,
    numberOfDaysInYear:
      deposit.number_of_days_in_year ??
      savings.interestCalculationDaysInYearTypeEnum ??
      null,
    nominalAnnualInterestRate: savings.nominalAnnualInterestRate ?? null,
  };
}

const getMifosProduct = async ({
  productType,
  tenantId,
}: TMifosProductQuery): Promise<TMifosProduct[]> => {
  let query = `?producttype=${productType}`;

  if (tenantId) {
    query = `${query}&TenantId=${tenantId}`;
  }

  const { data } = await getData(
    `${ajaxEndpoints.GET_MIFOS_PRODUCTS_DETAILS}${query}`,
  );

  // A missing or unknown tenant comes back as an object rather than an array,
  // still under a 200.
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map(normaliseMifosProduct);
};

export default function useMifosProduct({
  productType,
  tenantId,
}: TMifosProductQuery) {
  // Accepts an id, an enum name or a display name, so a caller holding a
  // created product's `deposit_type` string does not have to map it by hand.
  const depositTypeId = toDepositTypeId(productType);

  return useQuery<TMifosProduct[]>(
    [{ productType: depositTypeId, tenantId }, "getMifosProduct"],
    () => getMifosProduct({ productType: depositTypeId, tenantId }),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(tenantId) && depositTypeId !== undefined,
    },
  );
}
